import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Users, Heart, Calendar, Clock, CheckCircle, Sparkles } from 'lucide-react';
import groupsData from '../content/groups.json';

const iconMap = {
  Users: Users,
  Heart: Heart,
  Calendar: Calendar,
  Clock: Clock,
  CheckCircle: CheckCircle,
  Sparkles: Sparkles,
};

const GroupsSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const { sectionTitle, description, groups: groupsDataList, benefits: benefitsData, visionTitle, visionText } = groupsData;

  const groups = groupsDataList.map(group => ({
    ...group,
    icon: React.createElement(iconMap[group.icon] || Users, { size: 32 })
  }));

  const benefits = benefitsData.map(benefit => ({
    ...benefit,
    icon: React.createElement(iconMap[benefit.icon] || Heart, { size: 24 })
  }));

  return (
    <section id="groups" className="section-padding bg-gradient-to-b from-nitzan-light-bg to-white">
      <div className="container-width">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.div
            className="inline-flex items-center gap-2 bg-gradient-to-r from-nitzan-mint/20 to-nitzan-teal/20 px-6 py-3 rounded-full mb-6"
            initial={{ opacity: 0, y: -20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
          >
            <Users className="text-nitzan-teal" size={20} />
            <span className="text-nitzan-teal font-medium">קבוצות</span>
          </motion.div>

          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="gradient-text">{sectionTitle}</span>
          </h2>
          
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            {description}
          </p>
        </motion.div>

        {/* Groups Cards */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {groups.map((group, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 + index * 0.2, duration: 0.6 }}
              className="bg-white rounded-3xl shadow-xl overflow-hidden border border-nitzan-sage/20 hover:shadow-2xl transition-shadow duration-300"
            >
              <div className={`bg-gradient-to-r ${group.gradient} p-8 text-white`}>
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-4">
                  {group.icon}
                </div>
                <h3 className="text-3xl font-bold mb-3">{group.title}</h3>
                <p className="text-white/90 text-lg">{group.description}</p>
              </div>

              <div className="p-8">
                <div className="space-y-4 mb-6">
                  {group.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <CheckCircle className="text-nitzan-mint flex-shrink-0 mt-1" size={20} />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="border-t border-nitzan-sage/20 pt-6 mt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2 text-nitzan-teal">
                      <Calendar size={20} />
                      <span className="font-semibold">פתיחה קרובה</span>
                    </div>
                    <div className="flex items-center gap-2 text-nitzan-teal">
                      <Clock size={20} />
                      <span className="font-semibold">8 מפגשים</span>
                    </div>
                  </div>

                  <div className="bg-nitzan-sage/10 rounded-2xl p-4 mb-6">
                    <p className="text-center text-nitzan-terracotta font-semibold">
                      {group.price}
                    </p>
                  </div>

                  <Link to="/contact">
                    <motion.div
                      className={`block text-center bg-gradient-to-r ${group.gradient} text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300`}
                      whileHover={{ scale: 1.02, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      שריני מקום
                    </motion.div>
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Benefits */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="bg-gradient-to-br from-nitzan-mint/10 to-nitzan-teal/10 rounded-3xl p-8 md:p-12"
        >
          <h3 className="text-3xl font-bold text-nitzan-teal mb-8 text-center">למה קבוצה?</h3>
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.8 + index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-nitzan-mint to-nitzan-teal rounded-2xl flex items-center justify-center mx-auto mb-4 text-white">
                  {benefit.icon}
                </div>
                <h4 className="text-xl font-bold text-nitzan-teal mb-2">{benefit.title}</h4>
                <p className="text-gray-700">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Vision Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8, duration: 0.8 }}
          className="mt-16 text-center"
        >
          <div className="max-w-3xl mx-auto bg-white rounded-3xl p-8 md:p-12 shadow-xl border-2 border-nitzan-sage">
            <Sparkles className="text-nitzan-terracotta mx-auto mb-6" size={48} />
            <h3 className="text-3xl font-bold text-nitzan-teal mb-4">{visionTitle}</h3>
            <p className="text-xl text-gray-700 leading-relaxed">
              {visionText}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default GroupsSection;

